import React from 'react';
import { Link } from 'react-router-dom';

/*
 * Terms for Moneli's SEO, website and digital marketing services.
 *
 * HOW THESE ARE DELIVERED: a client never signs a long contract. Each quote
 * names the service, scope and price and links to that service's terms page
 * (/terms/<service>). Signing the quote, accepting it in writing, or paying
 * its deposit accepts the terms in force that day. Bump TERMS_VERSION and
 * EFFECTIVE_DATE whenever the text changes, so every quote can cite the
 * version it was accepted under.
 *
 * Draft for legal review before first use.
 */
export const TERMS_VERSION = '1.0';
export const EFFECTIVE_DATE = 'October 4, 2026';

/* ---- shared terms (every service) -------------------------------------- */
export const GENERAL = [
  {
    id: 'agreement', title: 'Agreement and acceptance',
    body: (
      <>
        <p>
          These terms apply to the service named in your quote, proposal or statement of work (the "Quote")
          from Moneli Automation Inc. ("Moneli", "we", "us"). The Quote and these terms together are the whole
          agreement between you and us for that service.
        </p>
        <ul>
          <li>You accept by signing the Quote, accepting it in writing (email is fine), or paying its deposit or first invoice.</li>
          <li>The Quote decides scope, deliverables, timeline and price. These terms decide everything else.</li>
          <li>The version of these terms in force on the day you accept applies to that Quote. We may update these terms for renewals or new Quotes with 30 days' written notice.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'fees', title: 'Fees and payment',
    body: (
      <ul>
        <li>Prices are in Canadian dollars and exclude HST and other taxes, which you pay.</li>
        <li>Invoices are due within 15 days. Monthly services are billed in advance on the 1st of each month.</li>
        <li>Overdue amounts bear interest at 1.5% per month (19.56% per year). After 15 days overdue we may pause work with written notice, and deadlines move by the length of the pause.</li>
        <li>Deposits reserve our schedule and are non-refundable once work has started. Fees for work already performed are non-refundable.</li>
        <li>Third-party costs, such as ad spend, hosting, domains, plugins, stock images and software licences, are paid by you directly or reimbursed to us at cost.</li>
      </ul>
    ),
  },
  {
    id: 'scope', title: 'Scope and changes',
    body: (
      <ul>
        <li>We perform only the work described in the Quote. Anything else is a change.</li>
        <li>We quote changes in writing and start them only after you approve. Email approval counts.</li>
        <li>Extra revision rounds and out-of-scope work are billed at the hourly rate in the Quote.</li>
      </ul>
    ),
  },
  {
    id: 'client', title: 'Your responsibilities',
    body: (
      <ul>
        <li>Provide content, logins, access and feedback by the dates in the Quote, or within 5 business days when no date is set. Your delays extend our deadlines by the same amount.</li>
        <li>You confirm you own or have permission to use everything you give us, such as text, images, logos and contact lists.</li>
        <li>You are responsible for the accuracy and legality of your business information, prices, offers and any regulated claims, including health, legal or financial claims.</li>
        <li>When you approve a design, page, campaign or piece of content, you accept it as final. Changes after approval are billed as new work.</li>
      </ul>
    ),
  },
  {
    id: 'results', title: 'No guaranteed results',
    body: (
      <ul>
        <li>Rankings, traffic, leads, sales and ad performance depend on things we do not control, including search engine and platform algorithms, competitors and your market. <strong>We do not guarantee any specific ranking, traffic, lead count, revenue or return on ad spend.</strong></li>
        <li>Fees are earned for the work performed, not for results.</li>
        <li>Google, Meta, hosting providers and other platforms may change their rules, prices or features, reject content, or suspend accounts. We are not responsible for their decisions, but we will tell you and help you respond. Your use of each platform is governed by that platform's own terms.</li>
      </ul>
    ),
  },
  {
    id: 'ownership', title: 'Ownership',
    body: (
      <ul>
        <li><strong>Once you have paid in full</strong>, you own the final deliverables made for you: website design, content we write for you, and final ad creative. Until then, we own them and you have a temporary licence to use them.</li>
        <li>We keep our pre-existing tools, code, templates, processes and know-how. You receive a permanent licence to use them as part of your delivered work.</li>
        <li>Third-party items such as themes, plugins, fonts and stock images remain under their own licences.</li>
        <li>Your domain, hosting, Google Business Profile, analytics and ad accounts are held in your name, or transferred to you on request once paid in full.</li>
        <li>We may show the finished work and your business name in our portfolio unless you tell us in writing not to.</li>
      </ul>
    ),
  },
  {
    id: 'confidentiality', title: 'Confidentiality',
    body: (
      <p>
        Each of us will keep the other's non-public business information confidential and use it only for this
        service, during the engagement and for 2 years after. This does not cover information that is public,
        already known, or required by law to be disclosed.
      </p>
    ),
  },
  {
    id: 'privacy', title: 'Privacy and anti-spam',
    body: (
      <ul>
        <li>When we handle personal information for you, such as form submissions, contact lists or analytics, we act on your behalf: we use it only to deliver the service, protect it with reasonable safeguards, and return or delete it when the engagement ends.</li>
        <li>You remain accountable for that information under PIPEDA and applicable provincial privacy laws, including your privacy policy and any consents required.</li>
        <li>We will notify you promptly if we learn of a security breach involving your personal information.</li>
        <li>Email and text messages are sent only to contacts for whom you confirm you hold consent under Canada's Anti-Spam Legislation (CASL). You keep the records of that consent.</li>
        <li>Our own handling of data is described in our <Link to="/privacy-policy">Privacy Policy</Link>.</li>
      </ul>
    ),
  },
  {
    id: 'termination', title: 'Term and termination',
    body: (
      <ul>
        <li>Monthly services run for the minimum term in the Quote, then month to month. Either of us may end them with 30 days' written notice after the minimum term.</li>
        <li>Either of us may end the agreement immediately if the other materially breaches these terms and does not fix the breach within 15 days of written notice.</li>
        <li>On termination you pay for all work done and costs incurred to that date. Ownership under "Ownership" transfers once that payment is made.</li>
        <li>We hand over files, logins and account access within 10 business days of final payment.</li>
      </ul>
    ),
  },
  {
    id: 'warranty', title: 'Warranty and remedy',
    body: (
      <p>
        We will perform the services professionally and in line with industry practice. If any work does not
        meet that standard and you tell us in writing within 30 days of delivery, we will, at our choice, redo
        that work at no charge or refund the fees paid for it. <strong>That is your only remedy for defective work.</strong> Apart from this, the services are provided without other warranties, to the extent the law allows.
      </p>
    ),
  },
  {
    id: 'liability', title: 'Limitation of liability',
    body: (
      <ul>
        <li><strong>Our total liability for all claims relating to a service is limited to the fees you paid us for that service in the 3 months before the event giving rise to the claim.</strong></li>
        <li>We are not liable for indirect, incidental or consequential losses, including lost profits, revenue, data, rankings or goodwill, or for losses caused by platform penalties, account suspensions or third-party outages, even if we were told they were possible.</li>
        <li>Any claim must be brought within 12 months of the event giving rise to it.</li>
        <li>These limits do not apply to fraud or wilful misconduct, or where the law does not allow them.</li>
      </ul>
    ),
  },
  {
    id: 'indemnity', title: 'Indemnity',
    body: (
      <p>
        You will indemnify Moneli against third-party claims, costs and penalties arising from content or
        materials you supplied or approved, your products and services, your advertising claims, or your
        contact lists and consents.
      </p>
    ),
  },
  {
    id: 'general', title: 'General',
    body: (
      <ul>
        <li><strong>Independent contractor.</strong> We are an independent contractor, not your employee, partner or agent.</li>
        <li><strong>Subcontractors.</strong> We may use subcontractors and remain responsible for their work.</li>
        <li><strong>Non-solicitation.</strong> During the engagement and for 12 months after, you will not hire anyone who worked on your project for us without our written consent.</li>
        <li><strong>Force majeure.</strong> Neither of us is liable for delays caused by events beyond reasonable control.</li>
        <li><strong>Notices.</strong> Notices are given in writing by email to the addresses in the Quote.</li>
        <li><strong>Governing law.</strong> These terms are governed by the laws of Ontario and the federal laws of Canada that apply there. We will first try to resolve any dispute in good faith for 30 days, then the courts of Ontario have jurisdiction.</li>
        <li><strong>Severability.</strong> If any term is unenforceable, the rest stays in effect.</li>
      </ul>
    ),
  },
  {
    id: 'contact', title: 'Contact',
    body: (
      <p>
        Questions about these terms: <a href="mailto:info@moneliautomation.com">info@moneliautomation.com</a> or{' '}
        <Link to="/contact">our contact page</Link>.
      </p>
    ),
  },
];

/* ---- service-specific terms -------------------------------------------- */
export const SERVICES = {
  seo: {
    name: 'SEO services',
    short: 'SEO',
    lead: 'Terms for search engine optimization: audits, on-page work, content, local listings and reporting.',
    sections: [
      {
        id: 'seo-scope', title: 'What SEO work covers',
        body: (
          <ul>
            <li>The Quote lists the monthly work: for example technical fixes, keyword research, on-page optimization, content, Google Business Profile management, citations and reporting.</li>
            <li>We send a monthly report of work completed and key measures such as rankings, organic traffic and enquiries.</li>
          </ul>
        ),
      },
      {
        id: 'seo-time', title: 'Timing and minimum term',
        body: (
          <ul>
            <li>SEO usually takes 3 to 6 months or longer to show measurable change. The minimum term in the Quote reflects this.</li>
            <li>Results can rise and fall after work stops, because search engines and competitors keep changing.</li>
          </ul>
        ),
      },
      {
        id: 'seo-site', title: 'Your website and listings',
        body: (
          <ul>
            <li>You give us the website and listing access needed. Where we cannot make a change ourselves, you or your web provider make it.</li>
            <li>We are not responsible for ranking or traffic losses caused by changes others make to your website, hosting or listings.</li>
            <li>We use only methods within search engine guidelines. We do not buy links or use practices that risk penalties.</li>
          </ul>
        ),
      },
    ],
  },
  'web-design': {
    name: 'Website design and redesign',
    short: 'Web design',
    lead: 'Terms for designing, building, redesigning and launching a website.',
    sections: [
      {
        id: 'web-process', title: 'Process and approvals',
        body: (
          <ul>
            <li>Projects run in stages: discovery, design, build and launch. You approve each stage before the next starts.</li>
            <li>The Quote states the number of pages and revision rounds included. Extra pages or rounds are billed as changes.</li>
            <li>You supply final text and images unless the Quote says we write or source them.</li>
          </ul>
        ),
      },
      {
        id: 'web-payment', title: 'Payment and launch',
        body: (
          <ul>
            <li>Unless the Quote says otherwise, 50% is due on acceptance and 50% before launch. We launch and hand over the site after final payment.</li>
            <li>If the project waits on you for more than 30 days, we may invoice for work completed. After 60 days we may close the project, and restarting it may carry a fee.</li>
          </ul>
        ),
      },
      {
        id: 'web-acceptance', title: 'Acceptance and fixes',
        body: (
          <ul>
            <li>The site is accepted when it launches, or 5 business days after we deliver it for review if you have not sent a written list of defects.</li>
            <li>For 30 days after launch we fix defects in our own work at no charge. This does not cover content changes, new features, third-party plugin or platform updates, hosting problems, or edits made by others.</li>
          </ul>
        ),
      },
      {
        id: 'web-after', title: 'After launch',
        body: (
          <ul>
            <li>Hosting, domain renewals, backups, security updates and maintenance are your responsibility unless you buy a maintenance plan from us.</li>
            <li>Where the Quote includes accessibility work, we build to WCAG 2.1 AA as a target. Ongoing compliance, including under Ontario's AODA where it applies to you, remains your responsibility, especially after content changes.</li>
          </ul>
        ),
      },
    ],
  },
  'digital-marketing': {
    name: 'Digital marketing',
    short: 'Digital marketing',
    lead: 'Terms for paid ads, social media, email marketing and campaign management.',
    sections: [
      {
        id: 'mkt-spend', title: 'Ad accounts and ad spend',
        body: (
          <ul>
            <li>Ads run on accounts in your name. You pay ad spend directly to the platform, separately from our management fee.</li>
            <li>We set budgets and caps as agreed in writing. Platforms may spend slightly above daily budgets under their own rules; we are not responsible for platform billing.</li>
          </ul>
        ),
      },
      {
        id: 'mkt-approval', title: 'Approvals and claims',
        body: (
          <ul>
            <li>You approve ad copy, creative, offers, landing pages and posts before they go live.</li>
            <li>You are responsible for your offers, prices, and any regulated or comparative claims in approved content.</li>
          </ul>
        ),
      },
      {
        id: 'mkt-platforms', title: 'Platform rules',
        body: (
          <ul>
            <li>Platforms can reject ads, limit reach or suspend accounts under their own policies. We will help you appeal or adjust, but we cannot guarantee any decision.</li>
            <li>Email and text campaigns require the CASL consent described in "Privacy and anti-spam".</li>
          </ul>
        ),
      },
      {
        id: 'mkt-fees', title: 'Management fees',
        body: (
          <p>
            Our monthly management fee is for the work performed and is payable regardless of campaign results or
            ad spend levels. Minimum term and notice are set out in the Quote and in "Term and termination".
          </p>
        ),
      },
    ],
  },
};

/** Number a list of sections 1..n for the legal layout. */
export const numbered = (sections) => sections.map((s, i) => ({ ...s, num: String(i + 1) }));
